import { UseSuspenseQueryOptions, useMutation, useQueryClient } from '@tanstack/react-query';

import { PublicationNewAliasRequest } from '@/module/Author/Publication/model/PublicationTypes';
import { TextFilters } from '@/module/Author/Text/model/TextTypes';

import { Id, PagedParams, PagedResponse, getQueryKey, invalidateAfterDelete } from '@feature/utils';

import * as api from './ChapterApi';
import { Chapter, ChapterRequest } from './ChapterTypes';

const chapterKey = getQueryKey('chapter');

export function getChapterPagedOptions(
  params: PagedParams<TextFilters>,
): UseSuspenseQueryOptions<PagedResponse<Chapter>> {
  return {
    queryKey: chapterKey.paged(params),
    queryFn: () => api.getPaged(params),
  };
}

export function getChapterOptions(chapterId: Id): UseSuspenseQueryOptions<Chapter> {
  return {
    queryKey: chapterKey.one(chapterId),
    queryFn: () => api.get(chapterId),
  };
}

export function useChapterEdit(chapterId: Id) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: ChapterRequest) => api.edit(chapterId, request),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: chapterKey.root(),
      }),
  });
}

export function useChapterCreate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: ChapterRequest) => api.create(request),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: chapterKey.root(),
      }),
  });
}

export function useChapterDelete(chapterId: Id) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.deleteById(chapterId),
    onSuccess: () => invalidateAfterDelete(queryClient, chapterKey, chapterId),
  });
}

export function useChapterAddNewAlias() {
  const queryClient = useQueryClient();
  return useMutation<Chapter, Error, { chapterId: Id; request: PublicationNewAliasRequest }>({
    mutationFn: ({ chapterId, request }) => api.addNewAlias(chapterId, request),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: chapterKey.root(),
      }),
  });
}
